import { Injectable } from '@angular/core';
import { ReportFormData, ReportType } from '../../views/report-details/interfaces/report-details.interface';

export interface GroupInfo {
    groupKey: string;
    groupValue: string;
    groupByColumnsList: number[];
    rows: any[];
    level: number;
    depth: number; // 1-based hierarchical nesting depth (1 = outermost group row)
    backgroundColor: string; // Row background color derived from the nesting depth
    totalRows: number;
    subgroups: GroupInfo[];
    groupSums: any;
    totalSum?: any; // Total sum for this group and all its subgroups
}

/**
 * Background colors for group header rows, keyed by 1-based nesting depth.
 * Depths beyond the last entry fall back to GROUP_ROW_DEFAULT_BACKGROUND_COLOR.
 */
const GROUP_ROW_BACKGROUND_COLORS: Record<number, string> = {
    1: '#bcd4ec',
    2: '#ecf6fc'
};

const GROUP_ROW_DEFAULT_BACKGROUND_COLOR = '#ffffff';

@Injectable({
    providedIn: 'root'
})
export class GroupingService {

    /**
     * Resolves the background color of a group row from its hierarchical nesting depth.
     * @param depth - 1-based nesting depth (1 = first level group row)
     */
    getGroupRowBackgroundColor(depth: number): string {
        const normalizedDepth = Number.isFinite(depth) && depth > 0 ? Math.floor(depth) : 1;
        return GROUP_ROW_BACKGROUND_COLORS[normalizedDepth] || GROUP_ROW_DEFAULT_BACKGROUND_COLOR;
    }


    /**
     * Creates hierarchical groups based on the specified level.
     * Group and row order follow the incoming dataset (first-seen keys, original
     * row sequence). Do not sort by groupByLevel or group name.
     * Sums cover the rows passed in, which is the current page of the grid.
     */
    createHierarchicalGroups(data: any[], reportFormData: ReportFormData, level: number, allColumns: Record<number, any>): GroupInfo[] {
        if (!data || data.length === 0) {
            return [];
        }

        const groupedData: GroupInfo[] = [];

        // Create nested maps based on level
        const groupMap = this.createNestedGroupMap(data, level, reportFormData, allColumns);

        // Build hierarchical structure
        this.buildHierarchicalStructure(groupMap, reportFormData, level, groupedData, allColumns, data);

        console.log(`Created ${level}-level grouped data:`, groupedData);
        return groupedData;
    }



    /**
     * Creates nested group maps for hierarchical grouping
     * @author: PRachethan | 14-07-2025
     */
    private createNestedGroupMap(
        data: any[],
        level: number,
        reportFormData: ReportFormData,
        allColumns: Record<number, any>
    ): Map<string, any> {
        const groupMap = new Map<string, any>();
        let groupByColumnsList: number[] = [];

        if (reportFormData.reportType == ReportType.SUMMARY_REPORT || reportFormData.reportType == ReportType.GRAPH_REPORT || reportFormData.reportType == ReportType.DIMENSION_REPORT) {
            groupByColumnsList = (reportFormData.groupByFilters || []) as number[];
        } else {
            groupByColumnsList = (reportFormData.columns?.slice(0, level) || []) as number[];
        }


        // Insert keys in first-seen dataset order so Map.forEach keeps DB sorting.
        data.forEach((row: any) => {
            let currentMap = groupMap;

            // Navigate through the nested structure
            for (let i = 0; i < level - 1; i++) {
                const displayValue = row[i] || '';
                const dbValue = this.getDatabaseValue(row, i, groupByColumnsList, allColumns);
                const key = dbValue || displayValue; // Use DB value for grouping, fallback to display value

                if (!currentMap.has(key)) {
                    // Create appropriate nested structure based on remaining levels
                    if (i === level - 2) {
                        currentMap.set(key, new Map<string, any[]>());
                    } else {
                        currentMap.set(key, new Map<string, any>());
                    }
                }

                currentMap = currentMap.get(key);
            }

            // Add the row to the final level
            const finalDisplayValue = row[level - 1] || '';
            const finalDbValue = this.getDatabaseValue(row, level - 1, groupByColumnsList, allColumns);
            const finalKey = finalDbValue || finalDisplayValue; // Use DB value for grouping, fallback to display value

            if (!currentMap.has(finalKey)) {
                currentMap.set(finalKey, []);
            }
            currentMap.get(finalKey).push(row);
        });

        return groupMap;
    }

    /**
     * Extracts display value for a given database value from the original data
     * @author: PRachethan | 14-07-2025
     */
    private getDisplayValueForDbValue(dbValue: string, columnIndex: number, originalData: any[], groupByColumnsList: number[], allColumns: Record<number, any>): string {
        for (const row of originalData) {
            if (row && Array.isArray(row)) {
                const rowDbValue = this.getDatabaseValue(row, columnIndex, groupByColumnsList, allColumns);
                if (rowDbValue === dbValue && row[columnIndex] !== undefined) {
                    return row[columnIndex];
                }
            }
        }
        return dbValue; // Fallback to database value if display value not found
    }

    /**
     * Extracts database value from row for the specified column index
     * @param row - Data row
     * @param columnIndex - Column index to extract DB value for
     * @param columns - Column metadata for mapping
     * @returns Database value or null if not found
     * @author: PRachethan | 14-07-2025
     */
    private getDatabaseValue(row: any[], columnIndex: number, groupByColumnsList: number[], allColumns: Record<number, any>): string | null {
        // Check if the last element is an object containing col__org_ keys
        const lastElement = row[row.length - 1];
        if (lastElement && typeof lastElement === 'object' && !Array.isArray(lastElement)) {
            // Get the column metadata for this index
            const columnId = groupByColumnsList[columnIndex];
            const columnInfo = allColumns[columnId];
            if (columnInfo) {
                // Extract value from col__org_ structure using column id
                const colKey = `col__org_${columnInfo.id}`;
                if (lastElement[colKey] !== undefined) {
                    // Return the database value from the col__org_ structure
                    return lastElement[colKey]?.toString() || null;
                }
            }
        }
        // If no col__org_ structure found or no column metadata, return the display value
        return row[columnIndex] || null;
    }

    /**
     * Builds hierarchical structure from nested maps
     * @author: PRachethan | 14-07-2025
     */
    private buildHierarchicalStructure(
        groupMap: Map<string, any>,
        reportFormData: ReportFormData,
        level: number,
        result: GroupInfo[],
        allColumns: Record<number, any>,
        originalData: any[]
    ): void {

        let groupByColumnsList: number[] = [];
        if (reportFormData.reportType == ReportType.SUMMARY_REPORT || reportFormData.reportType == ReportType.GRAPH_REPORT || reportFormData.reportType == ReportType.DIMENSION_REPORT) {
            groupByColumnsList = (reportFormData.groupByFilters || []) as number[];
        } else {
            groupByColumnsList = (reportFormData.columns?.slice(0, level) || []) as number[];
        }

        const unifiedColumns = this.getUnifiedTableColumns(reportFormData) || [];

        groupMap.forEach((value: any, key: string) => {
            if (level === 1) {
                // Single level grouping
                const groupSums = this.calculateGroupSums(value, unifiedColumns, allColumns, reportFormData);
                result.push(this.createGroupInfo(key, value, groupByColumnsList, 1, [], groupSums, allColumns, originalData, 0, 1, groupSums));
            } else {
                // Multi-level grouping
                const subgroups: GroupInfo[] = [];
                let totalRows = 0;
                let totalSums: any = {};

                this.buildSubgroups(value, reportFormData.columns || [], level - 1, subgroups, totalSums, totalRows, allColumns, originalData, reportFormData, 1);

                // Aggregate the whole subtree so the parent row reflects every nested subgroup
                const groupSums = this.aggregateHierarchicalSums(subgroups, unifiedColumns, allColumns, reportFormData);
                result.push(this.createGroupInfo(key, [], [groupByColumnsList[0]], 1, subgroups, groupSums, allColumns, originalData, 0, 1, groupSums));
            }
        });
    }

    /**
     * Builds subgroups recursively
     * @author: PRachethan | 14-07-2025
     */
    private buildSubgroups(
        groupMap: Map<string, any>,
        columnsList: (string | number)[],
        level: number,
        subgroups: GroupInfo[],
        totalSums: any,
        totalRows: number,
        allColumns: Record<number, any>,
        originalData: any[],
        reportFormData: ReportFormData,
        currentLevelIndex: number = 0
    ): void {
        let groupByColumnsList: number[] = [];
        if (reportFormData.reportType == ReportType.SUMMARY_REPORT || reportFormData.reportType == ReportType.GRAPH_REPORT || reportFormData.reportType == ReportType.DIMENSION_REPORT) {
            groupByColumnsList = (reportFormData.groupByFilters || []) as number[];
        } else {
            groupByColumnsList = (reportFormData.columns?.slice(0, level) || []) as number[];
        }

        const unifiedColumns = this.getUnifiedTableColumns(reportFormData) || [];
        const depth = currentLevelIndex + 1;

        groupMap.forEach((value: any, key: string) => {
            if (level === 1) {
                // Final level - create subgroup with actual rows
                const groupSums = this.calculateGroupSums(value, unifiedColumns, allColumns, reportFormData);
                const subgroup = this.createGroupInfo(key, value, groupByColumnsList, level, [], groupSums, allColumns, originalData, currentLevelIndex, depth, groupSums);
                subgroups.push(subgroup);
                totalRows += value.length;
                this.mergeSums(groupSums, totalSums);
            } else {
                // Intermediate level - create nested subgroups
                const nestedSubgroups: GroupInfo[] = [];
                let nestedTotalRows = 0;
                let nestedTotalSums: any = {};

                this.buildSubgroups(value, columnsList, level - 1, nestedSubgroups, nestedTotalSums, nestedTotalRows, allColumns, originalData, reportFormData, currentLevelIndex + 1);

                // Aggregate the whole subtree so this parent row includes every nested subgroup
                const groupSums = this.aggregateHierarchicalSums(nestedSubgroups, unifiedColumns, allColumns, reportFormData);
                const subgroup = this.createGroupInfo(key, [], groupByColumnsList, level, nestedSubgroups, groupSums, allColumns, originalData, currentLevelIndex, depth, groupSums);
                subgroups.push(subgroup);
                totalRows += nestedTotalRows;
                this.mergeSums(groupSums, totalSums);
            }
        });
    }

    /**
     * Creates standardized group info object
     * @author: PRachethan | 14-07-2025
     */
    private createGroupInfo(
        groupKey: string,
        rows: any[],
        groupByColumnsList: number[],
        level: number,
        subgroups: GroupInfo[],
        groupSums: any,
        allColumns: Record<number, any>,
        originalData?: any[],
        columnIndex?: number,
        depth: number = 1,
        totalSum?: any
    ): GroupInfo {

        // Extract display name from the first row if available
        let displayName = groupKey;

        // For intermediate levels (level 2, 3), rows might be empty but we have subgroups
        if (rows.length > 0 && rows[0] && Array.isArray(rows[0])) {
            // Use the display value from the appropriate column index
            const actualColumnIndex = columnIndex !== undefined ? columnIndex : level - 1;
            if (rows[0][actualColumnIndex] !== undefined) {
                displayName = rows[0][actualColumnIndex];
            }
        } else if (originalData && originalData.length > 0) {
            // For intermediate levels, extract display value from original data
            const actualColumnIndex = columnIndex !== undefined ? columnIndex : level - 1;
            displayName = this.getDisplayValueForDbValue(groupKey, actualColumnIndex, originalData, groupByColumnsList, allColumns);
        } else if (subgroups.length > 0) {
            // Fallback: try to get display name from subgroups
            for (const subgroup of subgroups) {
                if (subgroup.rows && subgroup.rows.length > 0 && subgroup.rows[0] && Array.isArray(subgroup.rows[0])) {
                    const actualColumnIndex = columnIndex !== undefined ? columnIndex : level - 1;
                    if (subgroup.rows[0][actualColumnIndex] !== undefined) {
                        displayName = subgroup.rows[0][actualColumnIndex];
                        break;
                    }
                }
            }
        }

        // groupSums already covers this group and its whole subtree, so it doubles as the total
        const finalTotalSum = totalSum || { ...groupSums };

        return {
            groupKey: groupKey, // Keep the database value as the key for grouping
            groupValue: displayName,// Use display name for display purposes
            groupByColumnsList,
            rows,
            level,
            depth,
            backgroundColor: this.getGroupRowBackgroundColor(depth),
            totalRows: rows.length || subgroups.reduce((sum: number, sg: GroupInfo) => sum + sg.totalRows, 0),
            subgroups,
            groupSums,
            totalSum: finalTotalSum
        };
    }

    getGroupByColumnsInfo(groupByColumnsList: number[], allColumns: Record<number, any>): Record<number, any> {
        return Object.values(allColumns).filter((column: any) => groupByColumnsList.includes(column.id));
    }

    /**
     * Calculates group sums for numeric and currency columns
     * @author: PRachethan | 14-07-2025
     */
    private isSummableColumn(columnInfo: any, columnId: any): boolean {
        // Skip counting for colDataType 3 (e.g. dimension text fields)
        if (columnInfo?.colDataType == 3) {
            return false;
        }
        if (columnId?.toString().startsWith('d')) {
            return true;
        }
        if (!columnInfo) {
            return false;
        }
        if (columnInfo.isPercentField == 1) {
            return false;
        }
        return columnInfo.colDataType == 1 || columnInfo.isAmountField == 1;
    }

    /**
     * Percent columns are not summed. Client Score Card (reportId == 8) and
     * summary reports still show the percent value on grouped record rows.
     */
    private shouldIncludePercentDisplayValue(reportFormData?: ReportFormData): boolean {
        if (Number(reportFormData?.reportId) === 8) {
            return true;
        }
        const reportType = reportFormData?.reportType;
        const numericReportType = typeof reportType === 'string' ? parseInt(reportType, 10) : Number(reportType);
        return numericReportType === ReportType.SUMMARY_REPORT;
    }

    private getNonAdditiveSumKeys(
        columnsList: any[],
        allColumns: Record<number, any>,
        reportFormData?: ReportFormData
    ): Set<string> {
        const keys = new Set<string>();
        if (!this.shouldIncludePercentDisplayValue(reportFormData)) {
            return keys;
        }

        (columnsList || []).forEach((columnId: any, index: number) => {
            const columnInfo = allColumns[columnId];
            if (columnInfo?.isPercentField == 1) {
                const sumColumnId = columnId?.toString().startsWith('d') ? columnId : columnInfo.id;
                keys.add(String(sumColumnId));
                keys.add(`sum_${index}`);
            }
        });

        return keys;
    }

    private getRowNumericValue(row: any[], index: number, sumColumnId: any): number {
        let value = 0;

        if (row[index] !== undefined && row[index] !== null && row[index] !== '') {
            value = parseFloat(String(row[index]).replace(/,/g, '')) || 0;
        }

        if (value === 0 && row[row.length - 1] && typeof row[row.length - 1] === 'object') {
            const colKey = `col__org_${sumColumnId}`;
            if (row[row.length - 1][colKey] !== undefined) {
                value = parseFloat(String(row[row.length - 1][colKey]).replace(/,/g, '')) || 0;
            }
        }

        return value;
    }

    private calculateGroupSums(
        rows: any[],
        columnsList: any[],
        allColumns: Record<number, any>,
        reportFormData?: ReportFormData
    ): any {
        const sums: any = {};
        const includePercentDisplay = this.shouldIncludePercentDisplayValue(reportFormData);

        if (!columnsList || columnsList.length === 0) {
            return sums;
        }

        // Initialize sums for numeric / currency columns using column codes for proper mapping
        columnsList.forEach((columnId: any, index: number) => {
            const columnInfo = allColumns[columnId];
            if (this.isSummableColumn(columnInfo, columnId)) {
                // Use column code for mapping to ensure proper column alignment
                sums[columnId?.toString().startsWith('d') ? columnId : columnInfo.id] = 0;
                // Also keep index-based mapping for backward compatibility
                sums[`sum_${index}`] = 0;
            } else if (includePercentDisplay && columnInfo?.isPercentField == 1) {
                // First-row display value for summary / Client Score Card percent columns
                sums[columnId?.toString().startsWith('d') ? columnId : columnInfo.id] = 0;
                sums[`sum_${index}`] = 0;
            }
        });

        // Calculate sums for each numeric / currency column
        rows.forEach((row: any, rowIndex: number) => {
            if (row && Array.isArray(row)) {
                columnsList.forEach((columnId: any, index: number) => {
                    const columnInfo = allColumns[columnId];
                    if (this.isSummableColumn(columnInfo, columnId)) {
                        const sumColumnId = columnId?.toString().startsWith('d') ? columnId : columnInfo.id;
                        const value = this.getRowNumericValue(row, index, sumColumnId);

                        // Map to both column code and index for flexibility
                        sums[sumColumnId] += value;
                        sums[`sum_${index}`] += value;
                    } else if (
                        includePercentDisplay &&
                        columnInfo?.isPercentField == 1 &&
                        rowIndex === 0
                    ) {
                        // Use the first row value for percent display (do not aggregate %)
                        const sumColumnId = columnId?.toString().startsWith('d') ? columnId : columnInfo.id;
                        const value = this.getRowNumericValue(row, index, sumColumnId);
                        sums[sumColumnId] = value;
                        sums[`sum_${index}`] = value;
                    }
                });
            }
        });

        return sums;
    }

    /**
     * Aggregates sums across an entire subgroup hierarchy.
     * Every group contributes the sums of its own rows plus, recursively, the sums of
     * all of its nested subgroups, so a parent row always reflects its full subtree.
     * @author: PRachethan | 14-07-2025
     */
    private aggregateHierarchicalSums(
        groups: GroupInfo[],
        columnsList: any[],
        allColumns: Record<number, any>,
        reportFormData?: ReportFormData
    ): any {
        const sums: any = {};
        const nonAdditiveKeys = this.getNonAdditiveSumKeys(columnsList, allColumns, reportFormData);

        (groups || []).forEach(group => {
            if (!group) {
                return;
            }

            if (group.rows && group.rows.length > 0) {
                this.mergeSums(this.calculateGroupSums(group.rows, columnsList, allColumns, reportFormData), sums, nonAdditiveKeys);
            }

            if (group.subgroups && group.subgroups.length > 0) {
                this.mergeSums(this.aggregateHierarchicalSums(group.subgroups, columnsList, allColumns, reportFormData), sums, nonAdditiveKeys);
            }
        });

        return sums;
    }

    /**
     * Adds every numeric entry of sourceSums into targetSums
     * @author: PRachethan | 14-07-2025
     */
    private mergeSums(sourceSums: any, targetSums: any, nonAdditiveKeys?: Set<string>): void {
        if (!sourceSums) {
            return;
        }

        Object.keys(sourceSums).forEach(key => {
            if (nonAdditiveKeys?.has(key)) {
                if (targetSums[key] === undefined) {
                    targetSums[key] = sourceSums[key];
                }
                return;
            }
            if (!targetSums[key]) {
                targetSums[key] = 0;
            }
            targetSums[key] += sourceSums[key] || 0;
        });
    }

    /**
     * Gets total sums for a specific group and all its subgroups
     * @param group - Group info object
     * @returns Total sums object
     * @author: PRachethan | 14-07-2025
     */
    getGroupTotalSums(group: GroupInfo): any {
        if (!group) {
            return {};
        }

        // If totalSum is already calculated, return it
        if (group.totalSum) {
            return group.totalSum;
        }

        const totalSum = { ...(group.groupSums || {}) };

        // groupSums of a parent already covers its subtree; only walk down when it is missing
        if (Object.keys(totalSum).length === 0 && group.subgroups && group.subgroups.length > 0) {
            group.subgroups.forEach(subgroup => {
                this.mergeSums(this.getGroupTotalSums(subgroup), totalSum);
            });
        }

        return totalSum;
    }







    // Legacy methods for backward compatibility
    /**
     * Creates single level groups
     * @author: PRachethan | 14-07-2025
     */
    createSingleLevelGroups(data: any[], reportFormData: ReportFormData, allColumns: Record<number, any>): GroupInfo[] {
        return this.createHierarchicalGroups(data, reportFormData, 1, allColumns);
    }

    /**
     * Creates two level groups
     * @author: PRachethan | 14-07-2025
     */
    createTwoLevelGroups(data: any[], reportFormData: ReportFormData, allColumns: Record<number, any>): GroupInfo[] {
        return this.createHierarchicalGroups(data, reportFormData, 2, allColumns);
    }

    /**
     * Creates three level groups
     * @author: PRachethan | 14-07-2025
     */
    createThreeLevelGroups(data: any[], reportFormData: ReportFormData, allColumns: Record<number, any>): GroupInfo[] {
        return this.createHierarchicalGroups(data, reportFormData, 3, allColumns);
    }


    getUnifiedTableColumns(reportFormData: ReportFormData): any[] {
        return [...(reportFormData?.groupByFilters || []), ...(reportFormData?.columns || [])];
    }



    formatToPercentage(value: string, column: any): string {
        if (column.isPercentField) {
            return parseFloat(value).toFixed(2) + '%';
        }
        return value.toString();
    }

    formatToAmount(value: number, column: any): string {
        if (column.isAmountField) {
            return value.toFixed(2);
        }
        return value.toString();
    }
} 